import { z } from 'zod';

/**
 * Validateurs Zod réutilisables pour les formulaires admin.
 *
 * Source de vérité front pour la validation des champs de profil
 * (joueur, club, organisation). Doit rester synchronisé avec le miroir
 * backend `apps/api/src/common/validation/patterns.ts`.
 *
 * Approche « pragmatique » : on bloque les caractères dangereux (chevrons
 * `< >` → anti-injection HTML/`<?php>`/`<script>`, caractères de contrôle)
 * partout, et on applique des formats stricts sur les champs structurés
 * (téléphone, code postal, licence). Les noms restent permissifs (accents,
 * tirets, apostrophes) pour ne pas rejeter les vrais noms.
 */

const FORBIDDEN = '<>\\u0000-\\u001F\\u007F';

export const PERSON_NAME_REGEX = /^[\p{L}\p{M}0-9 '’.-]+$/u;
export const PLACE_NAME_REGEX = /^[\p{L}\p{M}0-9 '’.,()-]+$/u;
export const ENTITY_NAME_REGEX = new RegExp(`^[^${FORBIDDEN}]+$`, 'u');
export const FREE_TEXT_REGEX = new RegExp(`^[^${FORBIDDEN}]*$`, 'u');
export const PHONE_REGEX = /^[0-9 +().-]{6,20}$/;
export const POSTCODE_REGEX = /^[0-9]{4,10}$/;
export const CITYCODE_REGEX = /^[0-9A-Za-z]{1,10}$/;
export const LICENSE_REGEX = /^[0-9]{1,20}$/;
export const USERNAME_REGEX = /^[a-z0-9._-]+$/;
export const URL_REGEX = /^https?:\/\/.+/i;
export const SIREN_REGEX = /^\d{9}$/;
export const SIRET_REGEX = /^\d{14}$/;

const MSG = {
  personName: 'Caractères non autorisés dans ce champ',
  placeName: 'Caractères non autorisés dans ce champ',
  entityName: 'Les caractères < et > ne sont pas autorisés',
  freeText: 'Les caractères < et > ne sont pas autorisés',
  phone: 'Numéro de téléphone invalide (chiffres, espaces, + ( ) . - uniquement)',
  postcode: 'Code postal invalide (4 à 10 chiffres)',
  citycode: 'Code INSEE invalide',
  license: 'Numéro de licence invalide (chiffres uniquement, sans espace)',
  username: "Nom d'utilisateur invalide (minuscules, chiffres, . - _ uniquement)",
  url: 'URL invalide (doit commencer par http:// ou https://)',
  siren: 'SIREN invalide (9 chiffres attendus)',
} as const;

export const SIREN_MESSAGE = MSG.siren;

// ─── Champs requis ────────────────────────────────────────────

/** Prénom / nom de personne, requis. */
export const personNameField = (label: string, max = 50) =>
  z
    .string()
    .trim()
    .min(1, `${label} est requis`)
    .max(max, `${label} : ${max} caractères maximum`)
    .regex(PERSON_NAME_REGEX, MSG.personName);

/** Nom de club / organisation, requis. */
export const entityNameField = (label: string, { min = 2, max = 100 }: { min?: number; max?: number } = {}) =>
  z
    .string()
    .trim()
    .min(min, `${label} requis (${min} caractères minimum)`)
    .max(max, `${label} : ${max} caractères maximum`)
    .regex(ENTITY_NAME_REGEX, MSG.entityName);

/** Nom de lieu (ville, département, région), requis. */
export const placeNameField = (label: string, max = 100) =>
  z
    .string()
    .trim()
    .min(1, `${label} requis`)
    .max(max, `${label} : ${max} caractères maximum`)
    .regex(PLACE_NAME_REGEX, MSG.placeName);

// ─── Champs optionnels (acceptent la chaîne vide) ─────────────

/** Nom de lieu optionnel. */
export const placeNameFieldOptional = (max = 100) =>
  z.string().trim().max(max).regex(PLACE_NAME_REGEX, MSG.placeName).optional().or(z.literal(''));

/** Texte libre optionnel (description, adresse). */
export const freeTextFieldOptional = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `${max} caractères maximum`)
    .regex(FREE_TEXT_REGEX, MSG.freeText)
    .optional()
    .or(z.literal(''));

/** Téléphone optionnel. */
export const phoneFieldOptional = z.string().trim().regex(PHONE_REGEX, MSG.phone).optional().or(z.literal(''));

/** Code postal optionnel. */
export const postcodeFieldOptional = z.string().trim().regex(POSTCODE_REGEX, MSG.postcode).optional().or(z.literal(''));

/** Code INSEE / citycode optionnel. */
export const citycodeFieldOptional = z.string().trim().regex(CITYCODE_REGEX, MSG.citycode).optional().or(z.literal(''));

/** Numéro de licence FFPJP optionnel. */
export const licenseFieldOptional = z.string().trim().regex(LICENSE_REGEX, MSG.license).optional().or(z.literal(''));

/** Nom d'utilisateur optionnel. */
export const usernameFieldOptional = z
  .string()
  .trim()
  .min(3, "Le nom d'utilisateur doit comporter au moins 3 caractères")
  .max(30, "Le nom d'utilisateur ne peut pas dépasser 30 caractères")
  .regex(USERNAME_REGEX, MSG.username)
  .optional()
  .or(z.literal(''));

/** Email optionnel. */
export const emailFieldOptional = z
  .string()
  .trim()
  .email('Adresse email invalide')
  .max(255, 'Email trop long')
  .optional()
  .or(z.literal(''));

/** URL optionnelle (http/https). */
export const urlFieldOptional = z.string().trim().max(255).regex(URL_REGEX, MSG.url).optional().or(z.literal(''));

/** Date ISO (YYYY-MM-DD) optionnelle. */
export const isoDateFieldOptional = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date invalide')
  .optional()
  .or(z.literal(''));

/** Spaces are accepted when pasting an identifier; letters and wrong lengths are rejected. */
export const siretFieldOptional = z
  .string()
  .transform(value => value.replace(/\s+/g, ''))
  .refine(value => value === '' || SIRET_REGEX.test(value), 'SIRET invalide (14 chiffres attendus)');
